import { Injectable } from '@angular/core';
import { LocalStorageManager } from '@examdojo/browser/local-storage';
import { TopicLevel2StoreModel } from '@examdojo/category/v2';
import {
  PracticeActivityQuery,
  PracticeActivityStoreModel,
  PracticeActivityStrategy,
} from '@examdojo/practice-activity';
import { QuestionAttemptEntityQuery, QuestionAttemptStoreModel, QuestionStoreModel } from '@examdojo/question';
import { distinctUntilArrayChanged, shareOneReplay } from '@examdojo/rxjs';
import { isNotNullish } from '@examdojo/util/nullish';
import { BehaviorSubject, distinctUntilChanged, filter, map, Observable, of, scan, switchMap } from 'rxjs';
import { TOPIC_PRACTICE_LOCAL_STORAGE_KEY } from './topic-practice-v2.model';

type QuestionsSequenceState = Array<{
  questionId: QuestionStoreModel['id'];
  lastAttempt?: QuestionAttemptStoreModel;
  isCompleted: boolean;
}>;

@Injectable({ providedIn: 'root' })
export class TopicPracticeV2Service {
  constructor(
    private readonly practiceActivityQuery: PracticeActivityQuery,
    private readonly questionAttemptQuery: QuestionAttemptEntityQuery,
  ) {}

  readonly topicLevel2SelectionStorageManager = new LocalStorageManager<{
    topicIds: Array<TopicLevel2StoreModel['id']>;
    strategy: PracticeActivityStrategy;
  }>(TOPIC_PRACTICE_LOCAL_STORAGE_KEY);

  readonly inProgressPracticeActivity$: Observable<PracticeActivityStoreModel | null> =
    this.practiceActivityQuery.inProgressPracticeActivityId$.pipe(
      switchMap((id) => (id ? this.practiceActivityQuery.selectEntity(id) : of(null))),
      filter(isNotNullish),
      shareOneReplay(),
    );

  private readonly isTransitioningBetweenQuestions$$ = new BehaviorSubject(false);
  readonly isTransitioningBetweenQuestions$ = this.isTransitioningBetweenQuestions$$.pipe(distinctUntilChanged());

  readonly candidates$ = this.practiceActivityQuery.candidates$;

  readonly selectedCandidate$ = this.inProgressPracticeActivity$.pipe(
    map((activity) => activity?.selected_candidate_id),
    distinctUntilChanged(),
    switchMap((selectedCandidateId) =>
      this.candidates$.pipe(map((candidates) => candidates?.find(({ id }) => id === selectedCandidateId))),
    ),
  );

  readonly questionsCount$ = this.selectedCandidate$.pipe(
    map((candidate) => candidate?.questionIds?.length ?? 0),
    distinctUntilChanged(),
  );

  readonly isMultiQuestionActivity$ = this.questionsCount$.pipe(
    map((count) => count > 1),
    distinctUntilChanged(),
  );

  readonly questionsSequence$: Observable<QuestionsSequenceState> = this.practiceActivityQuery.ongoingActivityId$.pipe(
    distinctUntilChanged(),
    switchMap(() =>
      this.selectedCandidate$.pipe(
        map((candidate) => candidate?.questionIds ?? []),
        distinctUntilArrayChanged(),
        switchMap((questionIds) =>
          this.questionAttemptQuery.active$.pipe(
            filter(isNotNullish),
            map(({ attempt }) => attempt),
            scan<QuestionAttemptStoreModel, Record<QuestionStoreModel['id'], QuestionAttemptStoreModel | undefined>>(
              (acc, activeAttempt) => ({ ...acc, [activeAttempt.question_id]: activeAttempt }),
              questionIds.reduce((acc, id) => ({ ...acc, [id]: undefined }), {}),
            ),
            map((lastAttemptByQuestion) =>
              questionIds
                .reduceRight((acc, questionId): QuestionsSequenceState => {
                  const attempt = lastAttemptByQuestion?.[questionId];
                  let isCompleted = false;

                  if (attempt) {
                    if (this.questionAttemptQuery.isQuestionMultipleChoice(attempt.id)) {
                      isCompleted = this.questionAttemptQuery.isMultipleChoiceQuestionGradedWithAwardedMarks(
                        attempt.id,
                      );
                    } else {
                      isCompleted = attempt.status === 'GRADED';
                    }
                  }

                  if (!isCompleted) {
                    isCompleted = acc.some((item) => item.isCompleted);
                  }

                  const current = {
                    questionId,
                    lastAttempt: attempt,
                    isCompleted,
                  };

                  return [current, ...acc];
                }, [] as QuestionsSequenceState)
                .map((item, index, arr): QuestionsSequenceState[number] => {
                  // If a question is missing an attempt, but subsequent questions have,
                  // then we consider it as completed, as its attempt has just not been fetched
                  if (
                    !item.isCompleted &&
                    !item.lastAttempt &&
                    arr.slice(index).some(({ lastAttempt }) => lastAttempt)
                  ) {
                    return { ...item, isCompleted: true };
                  }
                  return item;
                }),
            ),
          ),
        ),
      ),
    ),
    shareOneReplay(),
  );

  readonly areAllQuestionsCompleted$ = this.questionsSequence$.pipe(
    map((questionsState) => !!questionsState.length && questionsState.every((question) => question.isCompleted)),
    distinctUntilChanged(),
  );

  readonly nextIncompleteQuestion$ = this.questionsSequence$.pipe(
    map((questionsState) => questionsState.find((question) => !question.isCompleted)),
  );

  setTransitioning(value: boolean) {
    this.isTransitioningBetweenQuestions$$.next(value);
  }
}
